import type { ReactNode } from "react";

export type FrameContentMaxWidth = "xsmall" | "small" | "medium" | "large" | "xlarge" | "full";

export interface FrameContentProps {
  children: ReactNode;
  /** Cap on Content width inside Frame (Primer-style). Default `full`. */
  maxWidth?: FrameContentMaxWidth;
  /** Centre the content vertically and horizontally inside the scroll container. Used for error/auth screens. */
  centred?: boolean;
  /** Inset content from the viewport edge with responsive horizontal padding. Default `true`. */
  padded?: boolean;
  /**
   * Content fills the viewport exactly and owns every scroll inside it — the page itself never
   * scrolls at any width. For app-like surfaces with pinned chrome (the messages thread's docked
   * composer); a page whose content simply happens to be short does not want this.
   *
   * Content must then supply its own scroll container, because Frame's stops scrolling. A `Footer`
   * becomes unreachable, so pair with `hideFooter`.
   */
  fills?: boolean;
}

/**
 * Main content region. Frame wraps this in a `<main>` with its own scroll
 * container so panes can scroll independently and the content's bottom can be
 * reserved for the BottomBar without forcing a global page scroll.
 *
 * `maxWidth` caps content width following Primer's xsmall–xlarge ladder.
 * `centred` vertically/horizontally centres single-page content (error pages).
 * `padded` adds responsive horizontal padding (`px-5 md:px-8`) — true by default;
 * pages that want true edge-to-edge content (full-bleed heroes managed inline)
 * can opt out.
 */
export function FrameContent({ children }: FrameContentProps) {
  return <>{children}</>;
}
